import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import "./FoodMenuPerDay.css";
import API from "./config/API";

function FoodMenuPerDay() {
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));

  // Coming from FoodMenuList
  const menuId = location.state?.id || null;

  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [weekDayList, setWeekDayList] = useState([]);

  const [formData, setFormData] = useState({
    id: null,
    foodDayId: "",
    foodDay: "",
    createdBy: user?.emailId || "",
    adminId: user?.userId || "",
    createdDate: "",
    foodDayStatus: true,
  });

  const [menuList, setMenuList] = useState([]);

  // =========================================================
  // TIME OPTIONS
  // =========================================================

  /*
    Breakfast : 06 - 11
    Lunch     : 12 - 15
    Dinner    : 19 - 23
  */
  const getHoursByFoodType = (foodType) => {
    let start = 0;
    let end = 23;

    switch (foodType) {
      case "BREAK_FAST":
        start = 6;
        end = 11;
        break;

      case "LUNCH":
        start = 12;
        end = 15;
        break;

      case "DINNER":
        start = 19;
        end = 23;
        break;

      default:
        start = 0;
        end = 23;
        break;
    }

    return Array.from({ length: end - start + 1 }, (_, i) =>
      String(start + i).padStart(2, "0"),
    );
  };

  // Minutes 00 - 59
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    getWeekDays();

    if (menuId) {
      getFoodMenuDetails();
    } else {
      loadDefaultMenus();
    }
  }, []);

  // =========================================================
  // DEFAULT FOOD MENUS
  // =========================================================

  const loadDefaultMenus = () => {
    setMenuList([
      {
        menuId: null,
        foodMenuTypePerDay: "BREAK_FAST",
        foodMenu: "",
        extraFood: "",
        foodStatus: true,
        foodStartTime: "",
        foodEndTime: "",
      },
      {
        menuId: null,
        foodMenuTypePerDay: "LUNCH",
        foodMenu: "",
        extraFood: "",
        foodStatus: true,
        foodStartTime: "",
        foodEndTime: "",
      },
      {
        menuId: null,
        foodMenuTypePerDay: "DINNER",
        foodMenu: "",
        extraFood: "",
        foodStatus: true,
        foodStartTime: "",
        foodEndTime: "",
      },
    ]);
  };

  // =========================================================
  // TOAST
  // =========================================================

  const showToast = (message, type) => {
    setToast({
      show: true,
      message,
      type,
    });

    setTimeout(() => {
      setToast({
        show: false,
        message: "",
        type: "",
      });
    }, 2000);
  };

  // =========================================================
  // GET WEEK DAYS
  // =========================================================

  const getWeekDays = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || !user.jwtToken) {
      showToast("Session Expired. Please login again.", "error");
      navigate("/");
      return;
    }

    try {
      const response = await axios.get(
        `${API.V1}/foodMenu/getweekdays/${user.emailId}`,
        {
          headers: {
            Authorization: `Bearer ${user.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.statusCode === 200) {
        setWeekDayList(response.data.data);
      } else {
        showToast(
          response.data.message || "Unable to fetch weekdays.",
          "error",
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        showToast("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      console.error(error);

      showToast("Unable to fetch weekdays.", "error");
    }
  };

  // =========================================================
  // GET MENU DETAILS - EDIT
  // =========================================================

  const getFoodMenuDetails = async () => {
    setLoading(true);

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || !user.jwtToken) {
      showToast("Session Expired. Please login again.", "error");

      navigate("/");
      return;
    }

    try {
      const response = await axios.get(
        `${API.V1}/foodMenu/getFoodMenuDetailsByMenuId/${menuId}`,
        {
          headers: {
            Authorization: `Bearer ${user.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.statusCode === 200) {
        const data = response.data.data;

        setFormData({
          id: data.id,
          foodDayId: data.foodDayId,
          foodDay: data.foodDay,
          createdBy: data.createdBy,
          adminId: user.userId,
          createdDate: data.createdDate,
          foodDayStatus: data.foodDayStatus,
        });

        setMenuList(
          (data.foodTypesPerDay || []).map((item) => ({
            ...item,

            foodMenu: item.foodMenu || "",

            extraFood: item.extraFood || "",

            foodStatus: item.foodStatus ?? true,

            foodStartTime: item.foodStartTime || "",

            foodEndTime: item.foodEndTime || "",
          })),
        );
      } else {
        showToast(
          response.data.message || "Unable to load food menu.",
          "error",
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        showToast("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      console.error(error);

      showToast("Unable to load food menu.", "error");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FOOD DAY CHANGE
  // =========================================================

  const handleDayChange = (e) => {
    const selectedId = Number(e.target.value);

    const selectedDay = weekDayList.find(
      (item) => item.foodDayId === selectedId,
    );

    setFormData({
      ...formData,
      foodDayId: selectedId,
      foodDay: selectedDay?.foodDay || "",
    });
  };

  // =========================================================
  // MENU CHANGE
  // =========================================================

  const handleMenuChange = (index, field, value) => {
    const copy = [...menuList];

    copy[index] = {
      ...copy[index],
      [field]: value,
    };

    setMenuList(copy);
  };

  // =========================================================
  // GET TIME HOUR
  // =========================================================

  const getTimeHour = (time) => {
    if (!time) {
      return "";
    }

    return time.split(":")[0] || "";
  };

  // =========================================================
  // GET TIME MINUTE
  // =========================================================

  const getTimeMinute = (time) => {
    if (!time) {
      return "";
    }

    return time.split(":")[1] || "";
  };

  // =========================================================
  // UPDATE TIME
  // =========================================================

  const updateTime = (index, field, type, value) => {
    const currentTime = menuList[index][field] || "";

    let hour = getTimeHour(currentTime);
    let minute = getTimeMinute(currentTime);

    if (type === "hour") {
      hour = value;
    }

    if (type === "minute") {
      minute = value;
    }

    let finalTime = "";

    if (hour || minute) {
      finalTime = `${hour || "00"}:${minute || "00"}`;
    }

    handleMenuChange(index, field, finalTime);
  };

  // =========================================================
  // STATUS CHANGE
  // =========================================================

  const handleStatusChange = (status) => {
    setFormData({
      ...formData,
      foodDayStatus: status,
    });
  };

  // =========================================================
  // CARD COLORS
  // =========================================================

  const getCardColor = (type) => {
    switch (type) {
      case "BREAK_FAST":
        return "#FFF8E1";

      case "LUNCH":
        return "#E8F5E9";

      case "DINNER":
        return "#E3F2FD";

      default:
        return "#FFFFFF";
    }
  };

  // =========================================================
  // ICON
  // =========================================================

  const getIcon = (type) => {
    switch (type) {
      case "BREAK_FAST":
        return "🍳";

      case "LUNCH":
        return "🍛";

      case "DINNER":
        return "🍽";

      default:
        return "🍴";
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = (e) => {
    e.preventDefault();

    saveFoodMenu();
  };

  // =========================================================
  // SAVE FOOD MENU
  // =========================================================

  const saveFoodMenu = async () => {
    const payload = {
      id: menuId,

      foodDayId: formData.foodDayId,

      foodDay: formData.foodDay,

      createdBy: formData.createdBy,

      foodDayStatus: formData.foodDayStatus,

      foodMenuTypesPerDayRequest: menuList.map((item) => ({
        menuId: item.menuId,

        foodDayId: formData.foodDayId,

        foodMenuTypePerDay: item.foodMenuTypePerDay,

        foodMenu: item.foodMenu,

        extraFood: item.extraFood,

        foodStatus: item.foodStatus,

        foodStartTime: item.foodStartTime || "",

        foodEndTime: item.foodEndTime || "",
      })),
    };

    console.log("Payload :", payload);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await axios.post(`${API.V1}/foodMenu/create`, payload, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.statusCode === 200) {
        showToast(response.data.message, "success");

        setTimeout(() => {
          navigate("/foodMenuList");
        }, 2000);
      } else {
        showToast(response.data.message, "error");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session Expired. Please login again.");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      if (error.response?.data?.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("Internal Server Error", "error");
      }
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="loading-container">
        <h2>Loading Food Menu...</h2>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <>
      {/* TOAST */}
      {toast.show && (
        <div
          className={`center-popup ${
            toast.type === "success" ? "popup-success" : "popup-error"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="food-container">
        <div className="food-card">
          {/* PAGE TITLE */}
          <h2>
            {formData.id == null
              ? "🍽 Create Weekly Food Menu"
              : "🍽 Update Weekly Food Menu"}
          </h2>

          <form onSubmit={handleSubmit}>
            {/* =================================================
                FOOD DAY
                ================================================= */}

            <div className="top-row">
              <div className="field">
                <label>Food Day</label>

                <select
                  value={formData.foodDayId}
                  onChange={handleDayChange}
                  disabled={menuId}
                  required
                >
                  <option value="">Select Food Day</option>

                  {weekDayList.map((day) => (
                    <option
                      key={day.foodDayId}
                      value={day.foodDayId}
                      disabled={!menuId && day.isCreatedFoodperDay}
                    >
                      {day.foodDay}
                    </option>
                  ))}
                </select>
              </div>

              {/* STATUS */}

              {menuId && (
                <div className="field">
                  <label>Status</label>

                  <div className="status-box">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="foodDayStatus"
                        value="true"
                        checked={formData.foodDayStatus === true}
                        onChange={() => handleStatusChange(true)}
                      />
                      Active
                      <input
                        type="radio"
                        name="foodDayStatus"
                        value="false"
                        checked={formData.foodDayStatus === false}
                        onChange={() => handleStatusChange(false)}
                      />
                      Inactive
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* =================================================
                FOOD GRID
                ================================================= */}

            <div className="food-grid">
              {menuList.map((food, index) => (
                <div
                  className="food-edit-card"
                  key={food.menuId || index}
                  style={{
                    background: getCardColor(food.foodMenuTypePerDay),
                  }}
                >
                  {/* FOOD TITLE */}

                  <div className="food-title">
                    {getIcon(food.foodMenuTypePerDay)}{" "}
                    {food.foodMenuTypePerDay.replace("_", " ")}
                  </div>

                  {/* =================================================
                      MAIN MENU
                      ================================================= */}

                  <div className="food-section">
                    <label>Main Menu</label>

                    <textarea
                      value={food.foodMenu}
                      placeholder="Enter Main Menu"
                      onChange={(e) =>
                        handleMenuChange(index, "foodMenu", e.target.value)
                      }
                    />
                  </div>

                  {/* =================================================
                      EXTRA FOOD
                      ================================================= */}

                  <div className="food-section">
                    <label>Extra Food</label>

                    <textarea
                      value={food.extraFood}
                      placeholder="Enter Extra Food"
                      onChange={(e) =>
                        handleMenuChange(index, "extraFood", e.target.value)
                      }
                    />
                  </div>

                  {/* =================================================
                      FOOD TIMING
                      ================================================= */}

                  <div className="food-time-section">
                    <label className="food-time-heading">Food Timing</label>

                    {/* =================================================
                        START TIME
                        ================================================= */}

                    <div className="time-row">
                      <label className="time-label">Start Time</label>

                      {/* START HOUR */}

                      <select
                        className="time-select"
                        value={getTimeHour(food.foodStartTime)}
                        onChange={(e) =>
                          updateTime(
                            index,
                            "foodStartTime",
                            "hour",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Hour</option>

                        {getHoursByFoodType(food.foodMenuTypePerDay).map(
                          (hour) => (
                            <option key={hour} value={hour}>
                              {hour}
                            </option>
                          ),
                        )}
                      </select>

                      <span className="time-colon">:</span>

                      {/* START MINUTE */}

                      <select
                        className="time-select"
                        value={getTimeMinute(food.foodStartTime)}
                        onChange={(e) =>
                          updateTime(
                            index,
                            "foodStartTime",
                            "minute",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Min</option>

                        {minutes.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* =================================================
                        END TIME
                        ================================================= */}

                    <div className="time-row">
                      <label className="time-label">End Time</label>

                      {/* END HOUR */}

                      <select
                        className="time-select"
                        value={getTimeHour(food.foodEndTime)}
                        onChange={(e) =>
                          updateTime(
                            index,
                            "foodEndTime",
                            "hour",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Hour</option>

                        {getHoursByFoodType(food.foodMenuTypePerDay).map(
                          (hour) => (
                            <option key={hour} value={hour}>
                              {hour}
                            </option>
                          ),
                        )}
                      </select>

                      <span className="time-colon">:</span>

                      {/* END MINUTE */}

                      <select
                        className="time-select"
                        value={getTimeMinute(food.foodEndTime)}
                        onChange={(e) =>
                          updateTime(
                            index,
                            "foodEndTime",
                            "minute",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Min</option>

                        {minutes.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* =================================================
                BUTTONS
                ================================================= */}

            <div className="button-row">
              <button type="submit" className="save-btn">
                {formData.id == null ? "Save Food Menu" : "Update Food Menu"}
              </button>

              {formData.id != null && (
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate("/foodMenuList")}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default FoodMenuPerDay;
